import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../auth/context";
import ApiCall from "../auth/ApiCall";

const COOLDOWN_MS = 30 * 60 * 1000;
const LAST_CHECK_KEY = "update_msg_checked_at";
const LAST_SEEN_KEY = "update_msg_last_seen";

interface UpdateMessage {
  title: string;
  body: string;
  key: string;
}

export default function UpdateMessageBanner() {
  const { user } = useAuth();
  const [message, setMessage] = useState<UpdateMessage | null>(null);
  const [visible, setVisible] = useState(false);

  const check = useCallback(async () => {
    if (!user) return;

    const lastCheck = parseInt(localStorage.getItem(LAST_CHECK_KEY) || "0", 10);
    if (Date.now() - lastCheck < COOLDOWN_MS) return;

    localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));

    const response = (await ApiCall.makeAuthenticatedRequest(
      "/accounts/api/update-message/",
    )) as {
      error?: string;
      message?: {
        id: number;
        title: string;
        body: string;
        created_at: string;
      } | null;
    };

    if (response.error || !response.message) return;

    const key = String(response.message.id || response.message.body);
    if (localStorage.getItem(LAST_SEEN_KEY) === key) return;

    setMessage({
      title: response.message.title || "What's New",
      body: response.message.body,
      key,
    });
    setVisible(true);
  }, [user]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [check]);

  const dismiss = () => {
    if (message) localStorage.setItem(LAST_SEEN_KEY, message.key);
    setVisible(false);
    setTimeout(() => setMessage(null), 300);
  };

  if (!message) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(3px)",
        zIndex: 1000,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.3s ease",
        pointerEvents: visible ? "auto" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          backgroundColor: "#050510",
          borderRadius: 16,
          padding: "20px 24px",
          boxShadow: "0 0 0 3px #0032c4, 0 8px 32px rgba(0, 50, 196, 0.35)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          position: "relative",
        }}
      >
        {/* Dismiss button */}
        <button
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 14,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#666",
            fontSize: 20,
            lineHeight: 1,
            padding: 4,
          }}
          aria-label="Dismiss"
        >
          ×
        </button>

        <p
          className="font_family_base"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#4a9eff",
            textTransform: "uppercase",
            letterSpacing: 1,
            margin: 0,
            textAlign: "center",
          }}
        >
          {message.title}
        </p>

        <p
          className="font_family_base"
          style={{
            fontSize: 15,
            color: "#e0e0e0",
            lineHeight: 1.5,
            margin: 0,
            paddingRight: 20,
          }}
        >
          {message.body}
        </p>

        <button
          onClick={dismiss}
          className="font_family_base"
          style={{
            alignSelf: "flex-end",
            marginTop: 4,
            background: "none",
            border: "1px solid #333",
            borderRadius: 100,
            color: "#888",
            fontSize: 13,
            padding: "4px 16px",
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
